import { maskCpf } from "@/utils/cpf-mask";
import { useMemo, useState } from "react";
import { Check, Pencil, Plus, Search, Trash2, X } from "lucide-react";

import { Button, buttonVariants } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";

import {
  dedupeCpfs,
  isCompleteValidCpf,
  normalizeCpf,
} from "../utils/segmentation";
import { CpfPaginationFooter } from "./cpf-pagination-footer";
import {
  CPF_PAGE_SIZES,
  useCpfPagination,
  type CpfPageSize,
} from "./use-cpf-pagination";

const BROWSE_THRESHOLD = 8;

interface CpfSegmentationListProps {
  cpfs: string[];
  onChange: (cpfs: string[]) => void;
  disabled?: boolean;
}

type PendingConfirm =
  | { kind: "one"; cpf: string }
  | { kind: "all" }
  | null;

export function CpfSegmentationList({
  cpfs,
  onChange,
  disabled = false,
}: CpfSegmentationListProps) {
  const [newCpf, setNewCpf] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [editingCpf, setEditingCpf] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [editError, setEditError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm>(null);

  const filterDigits = normalizeCpf(filter);
  const filtered = useMemo(() => {
    if (!filterDigits) return cpfs;
    return cpfs.filter((cpf) => cpf.includes(filterDigits));
  }, [cpfs, filterDigits]);

  const pagination = useCpfPagination(filtered);
  const showBrowseTools: boolean =
    cpfs.length > BROWSE_THRESHOLD || filter !== "";

  const commit = (next: string[]) => onChange(dedupeCpfs(next));

  const handleFilterChange = (value: string) => {
    setFilter(value);
    pagination.setPage(1);
  };

  const handleAdd = () => {
    const digits = normalizeCpf(newCpf);
    if (!isCompleteValidCpf(digits)) {
      setAddError("CPF inválido — informe os 11 dígitos.");
      return;
    }
    if (cpfs.includes(digits)) {
      setAddError("Esse CPF já está na base.");
      return;
    }
    commit([...cpfs, digits]);
    setNewCpf("");
    setAddError(null);
  };

  const startEdit = (cpf: string) => {
    setEditingCpf(cpf);
    setEditingValue(maskCpf(cpf));
    setEditError(null);
  };

  const cancelEdit = () => {
    setEditingCpf(null);
    setEditingValue("");
    setEditError(null);
  };

  const saveEdit = (original: string) => {
    const digits = normalizeCpf(editingValue);
    if (!isCompleteValidCpf(digits)) {
      setEditError("CPF inválido.");
      return;
    }
    if (digits !== original && cpfs.includes(digits)) {
      setEditError("Esse CPF já está na base.");
      return;
    }
    commit(cpfs.map((cpf) => (cpf === original ? digits : cpf)));
    cancelEdit();
  };

  const removeCpf = (cpf: string) => {
    commit(cpfs.filter((item) => item !== cpf));
  };

  const confirmPending = () => {
    if (!pendingConfirm) return;
    if (pendingConfirm.kind === "all") {
      commit([]);
      setFilter("");
    } else {
      removeCpf(pendingConfirm.cpf);
    }
    setPendingConfirm(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium">
          Base de CPFs: <strong>{cpfs.length.toLocaleString("pt-BR")}</strong>
        </p>
        {cpfs.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700"
            disabled={disabled}
            onClick={() => setPendingConfirm({ kind: "all" })}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Limpar tudo
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <div className="flex-1">
          <Label htmlFor="cpf-add-input" className="sr-only">
            Adicionar CPF
          </Label>
          <Input
            id="cpf-add-input"
            aria-label="Adicionar CPF"
            inputMode="numeric"
            placeholder="000.000.000-00"
            value={newCpf}
            disabled={disabled}
            onChange={(event) => {
              setNewCpf(maskCpf(event.target.value));
              if (addError) setAddError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleAdd();
              }
            }}
          />
          {addError && <p className="mt-1 text-sm text-red-500">{addError}</p>}
        </div>
        <Button type="button" onClick={handleAdd} disabled={disabled}>
          <Plus className="mr-2 h-4 w-4" />
          Adicionar
        </Button>
      </div>

      {showBrowseTools && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              aria-label="Buscar CPF na base"
              className="pl-9"
              placeholder="Buscar na base..."
              value={filter}
              onChange={(event) => handleFilterChange(event.target.value)}
            />
            {filter && (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                aria-label="Limpar busca"
                className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
                onClick={() => handleFilterChange("")}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="cpf-page-size" className="mb-0 whitespace-nowrap text-sm">
              Por página
            </Label>
            <select
              id="cpf-page-size"
              className="h-10 rounded-md border border-input bg-background px-2 text-sm"
              value={pagination.pageSize}
              onChange={(event) =>
                pagination.setPageSize(Number(event.target.value) as CpfPageSize)
              }
            >
              {CPF_PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {cpfs.length === 0 && (
        <p className="rounded-lg border border-dashed p-4 text-center text-sm text-gray-500">
          Nenhum CPF na base. Importe um arquivo ou adicione manualmente.
        </p>
      )}

      {cpfs.length > 0 && (
        <div className="max-h-72 overflow-auto rounded-lg border">
          <ul className="divide-y">
            {pagination.pageItems.map((cpf) => (
              <li
                key={cpf}
                className="flex items-center justify-between gap-2 px-3 py-2"
              >
                {editingCpf === cpf ? (
                  <div className="flex flex-1 flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Input
                        aria-label={`Editar CPF ${maskCpf(cpf)}`}
                        inputMode="numeric"
                        className="h-8"
                        value={editingValue}
                        onChange={(event) => {
                          setEditingValue(maskCpf(event.target.value));
                          if (editError) setEditError(null);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            saveEdit(cpf);
                          }
                          if (event.key === "Escape") cancelEdit();
                        }}
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label="Salvar edição"
                        className="h-8 w-8 text-green-600"
                        onClick={() => saveEdit(cpf)}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label="Cancelar edição"
                        className="h-8 w-8"
                        onClick={cancelEdit}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {editError && (
                      <p className="text-sm text-red-500">{editError}</p>
                    )}
                  </div>
                ) : (
                  <>
                    <span className="font-mono text-sm">{maskCpf(cpf)}</span>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label={`Editar CPF ${maskCpf(cpf)}`}
                        className="h-8 w-8"
                        disabled={disabled}
                        onClick={() => startEdit(cpf)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label={`Remover CPF ${maskCpf(cpf)}`}
                        className="h-8 w-8 text-red-600"
                        disabled={disabled}
                        onClick={() =>
                          setPendingConfirm({ kind: "one", cpf })
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
          {filtered.length === 0 && (
            <p className="px-3 py-4 text-center text-sm text-gray-500">
              Nenhum CPF corresponde à busca.
            </p>
          )}
        </div>
      )}

      {showBrowseTools && filtered.length > 0 && (
        <CpfPaginationFooter
          page={pagination.page}
          pageCount={pagination.pageCount}
          firstItemIndex={pagination.firstItemIndex}
          lastItemIndex={pagination.lastItemIndex}
          filteredCount={filtered.length}
          totalCount={cpfs.length}
          onPageChange={pagination.setPage}
        />
      )}

      <AlertDialog
        open={pendingConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setPendingConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingConfirm?.kind === "all"
                ? "Limpar toda a base de CPFs?"
                : "Remover este CPF?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingConfirm?.kind === "all"
                ? `Os ${cpfs.length} CPF(s) da base serão removidos. Essa ação pode ser desfeita reimportando o arquivo.`
                : `O CPF ${
                    pendingConfirm?.kind === "one"
                      ? maskCpf(pendingConfirm.cpf)
                      : ""
                  } será removido da base.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: "destructive" })}
              onClick={confirmPending}
            >
              {pendingConfirm?.kind === "all" ? "Limpar tudo" : "Remover"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
